import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from './ApiProvider';
import type { ApiClient } from './client';

export interface QueryState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  /** True while a `reload()` refetches the same query: `data` is still current, so keep showing it. */
  refreshing: boolean;
  reload: () => void;
}

/**
 * Minimal data hook: runs `fn` whenever `deps` change, aborts the previous request, and exposes a
 * `reload`. Small enough that a query library is not worth its weight for this app.
 */
export function useApiQuery<T>(
  fn: (api: ApiClient, signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: { enabled?: boolean } = {},
): QueryState<T> {
  const api = useApi();
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const reloadRequested = useRef(false);
  const lastDeps = useRef<readonly unknown[]>(deps);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const sameQuery = deps.length === lastDeps.current.length && deps.every((d, i) => Object.is(d, lastDeps.current[i]));
    lastDeps.current = deps;
    const isRefresh = reloadRequested.current && sameQuery;
    reloadRequested.current = false;
    const controller = new AbortController();
    setRefreshing(isRefresh);
    setLoading(true);
    setError(null);
    fnRef
      .current(api, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setData(result);
          setLoading(false);
          setRefreshing(false);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
        setRefreshing(false);
      });
    return () => controller.abort();
  }, [api, enabled, tick, ...deps]);

  const reload = useCallback(() => {
    reloadRequested.current = true;
    setTick((t) => t + 1);
  }, []);
  return { data, error, loading, refreshing, reload };
}
